import { Contact, ContactStatus } from '@wonderschool/common-base-types';
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  ReInviteIcon,
  Send2Icon,
  Tooltip,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import CN from 'classnames';
import { useMemo, useState } from 'react';
import Avatar from 'react-avatar';
import BootstrapTable from 'react-bootstrap-table-next';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Header, Icon, Segment } from 'semantic-ui-react';
import { userHasPermission } from '../../../api/firebase/account';
import {
  ContactStatusLabel,
  SendInvitationConfirmationDialog,
  showInvitationErrorToast,
  showInvitationSuccessToast,
} from '../../../contacts';
import { inviteContact } from '../../../contacts/contactsAPI';
import { useContacts } from '../../../contacts/contactsHooks';
import { formatFullName, phoneNumberParse } from '../../../helpers/utils';
import { useOrganization } from '../../../hooks/useOrganizations';
import { useUser } from '../../../hooks/useUser';
import { RELATIONSHIP_TYPES } from '../../consts';
import { createContactDataForDelete, createResponsibleForBillingDataForDelete, saveStudent } from '../../studentsAPI';
import { useSelectedStudent } from '../../studentsHooks';
import { getOrCreateRelationshipNotes } from '../../studentsUtils';
import DeleteContactConfirmationDialog from './DeleteContactConfirmationDialog';
import { FamilyEditContactModal } from './FamilyEditContactModal';

export default function FamilyList() {
  const { t } = useTranslation();

  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [sendToContact, setSendToContact] = useState(null);

  const { isParent } = useUser();
  const canRoleEditStudentContacts = userHasPermission('can_edit_student_contacts');
  const organization = useOrganization();
  const student = useSelectedStudent();
  const { familyContacts } = useContacts();

  const contacts = useMemo(() => {
    if (isParent) {
      return Object.keys(student.family || {}).map((contactId) => ({
        id: contactId,
        ...student.family[contactId],
      }));
    }

    const familyMembers = student?.familyMembers || [];

    return familyContacts.filter((contact) => familyMembers.includes(contact.id));
  }, [student?.familyMembers, familyContacts, isParent, student?.family]);

  const removeContactFromStudent = async (contactToRemove) => {
    const isResponsible = student.responsibleForBilling && student.responsibleForBilling.id === contactToRemove.id;
    const responsibleData = isResponsible ? createResponsibleForBillingDataForDelete() : {};

    const data = {
      id: student.id,
      ...createContactDataForDelete(contactToRemove),
      ...responsibleData,
    };

    return saveStudent(organization.id, data);
  };

  const isResponsibleForBilling = (contact: Contact | null) => {
    return contact?.id === student.responsibleForBilling?.id;
  };

  return (
    <>
      {contacts.length ? renderTable() : renderNoData()}

      <FamilyEditContactModal
        student={student}
        contact={selectedContact}
        isOpen={isEditContactModalOpen}
        onClose={onCloseEditContactModal}
        onReplaceExistingContact={removeContactFromStudent}
      />
      <DeleteContactConfirmationDialog
        isOpen={isDeleteConfirmationDialogOpen}
        isResponsibleForBilling={isResponsibleForBilling(selectedContact)}
        onClose={() => setIsDeleteConfirmationDialogOpen(false)}
        onYes={async () => {
          setIsDeleteConfirmationDialogOpen(false);
          return removeContactFromStudent(selectedContact);
        }}
      />
      <SendInvitationConfirmationDialog
        onClose={() => setSendToContact(null)}
        onYes={onSendInvitation}
        contact={sendToContact}
      />
    </>
  );

  function renderTable() {
    return (
      <div className="bootstrap-iso">
        <BootstrapTable
          bootstrap4
          classes="w-auto w-md-100"
          wrapperClasses="table-responsive"
          noDataIndication={t('common.noResultsToDisplay')}
          keyField="id"
          bordered={false}
          data={contacts}
          columns={getTableColumns()}
        />
      </div>
    );
  }

  function renderNoData() {
    return (
      <Segment basic clearing textAlign="center" className="no-shadow no-border top-border">
        <Header as={'h3'}>
          <Icon circular inverted name="info" color="grey" />
        </Header>
        <Header as={'h4'}>{t('familyList.noContactAdded')}</Header>
        <p className="text-muted">{t('familyList.instructions')}</p>
      </Segment>
    );
  }
  function onClickEdit(contact) {
    setSelectedContact(contact);
    setIsEditContactModalOpen(true);
  }
  function onCloseEditContactModal() {
    setSelectedContact(null);
    setIsEditContactModalOpen(false);
  }
  function onClickDelete(contact) {
    setSelectedContact(contact);
    setIsDeleteConfirmationDialogOpen(true);
  }
  function onClickSend(contact) {
    setSendToContact(contact);
  }
  async function onSendInvitation(contact) {
    try {
      contact.status = ContactStatus.inviting;
      await inviteContact(contact);
      showInvitationSuccessToast(contact);
    } catch (error) {
      console.log(error);
      showInvitationErrorToast(contact, error);
    } finally {
      setSendToContact(null);
    }
    return null;
  }

  function getTableColumns() {
    return [
      {
        dataField: 'id',
        text: 'id',
        hidden: true,
      },
      {
        text: '',
        dataField: 'avatar',
        formatter: (cell, row) => {
          return (
            <Avatar
              name={formatFullName(row, true)}
              round
              size={'48'}
              className="left floated"
              src={row?.photoURL || row?.picture}
            />
          );
        },
        headerStyle: () => {
          return { width: '60px' };
        },
      },
      {
        dataField: 'displayName',
        formatter: (cell, row, _rowIndex, _formatExtraData) => (
          <div>
            <div
              className={CN('textTruncate', 'family-member-name')}
              style={{ fontSize: '14px', fontWeight: 'bolder' }}
              data-testid="family-member-name"
            >
              {cell || formatFullName(row)}
            </div>
            <div>
              {/* Hide bogus emails because they confuse users. */}
              {row.email && !row.email.startsWith('moxit_') ? (
                <Tooltip content={row.email} action={'hover'}>
                  <div className={CN('textTruncate', 'family-member-email')} data-testid="family-member-email">
                    {row.email}
                  </div>
                </Tooltip>
              ) : (
                ''
              )}
            </div>
          </div>
        ),
        text: t('common.name'),
        sort: true,
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        headerStyle: () => {
          return { width: '300px' };
        },
      },
      {
        dataField: 'phone',
        text: t('common.phone'),
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        formatter: (cell) => phoneNumberParse(cell),
        headerStyle: () => {
          return { width: '130px' };
        },
      },
      {
        dataField: 'relationshipNotes',
        text: t('common.relationship'),
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        formatter: (cell, row) => getOrCreateRelationshipNotes(row, t),
        headerStyle: () => {
          return { width: '165px' };
        },
      },
      {
        dataField: 'allowLoginWithPin',
        text: t('common.pickup'),
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        formatter: (cell) =>
          cell ? <CheckmarkIcon className="size-8 text-green-800" /> : <CloseIcon className="size-8 text-red-800" />,
        headerStyle: () => {
          return { width: '60px' };
        },
      },
      {
        dataField: 'status',
        text: t('common.status'),
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        formatter: (_cell, row) => <ContactStatusLabel contact={row} />,
        headerStyle: () => {
          return { width: '80px' };
        },
      },
      {
        dataField: 'responsibleForBilling',
        text: t('Payment'),
        headerAlign: 'left',
        style: { verticalAlign: 'middle' },
        formatter: (cell, row) => {
          if (row.id === student.responsibleForBilling?.id) {
            return t('parentsListing.primary');
          }
          return null;
        },
        headerStyle: () => {
          return { width: '60px' };
        },
      },
      // parents, teachers, support staff cannot edit or delete family members
      ...(!canRoleEditStudentContacts
        ? []
        : [
            {
              text: t('familyList.invitation'),
              dataField: '',
              headerAlign: 'center',
              style: { verticalAlign: 'middle' },
              formatter: (_cell, row: Contact) => {
                const isAuthorizedToPickup =
                  row.relationship === RELATIONSHIP_TYPES.parent ||
                  (row.relationship === RELATIONSHIP_TYPES.contact && row.allowLoginWithPin);

                return (
                  <div>
                    {row.status === ContactStatus.invited && (
                      <Tooltip content={t('familyList.reinviteParent')} action={'hover'}>
                        <span>
                          <Button
                            size={WidgetSizeEnum.X_SMALL}
                            onClick={() => onClickSend(row)}
                            data-testid="re-invite-parent-btn"
                            extraClasses="rounded-full ring-0 ring-offset-0 shadow-none"
                          >
                            <ReInviteIcon className="size-6" />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {row.status === ContactStatus.new && isAuthorizedToPickup && (!row.uid || row.uid === row.id) && (
                      <Tooltip
                        content={row.email ? t('familyList.inviteParentToJoin') : t('familyList.addEmailToInvite')}
                        action={'hover'}
                      >
                        <span>
                          <Button
                            size={WidgetSizeEnum.X_SMALL}
                            // Primary because this button is main CTA to get the parent to join the app
                            primary
                            disabled={!row.email}
                            onClick={() => {
                              if (row.email) onClickSend(row);
                            }}
                            data-testid="send-invite-btn"
                            extraClasses="rounded-full ring-0 ring-offset-0 shadow-none"
                          >
                            <Send2Icon className="size-6" />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {row.status === ContactStatus.inviting && (
                      <Tooltip content={t('familyList.currentlyInvitingParent')} action={'hover'}>
                        <span>
                          <Button
                            size={WidgetSizeEnum.X_SMALL}
                            extraClasses="rounded-full ring-0 ring-offset-0 shadow-none"
                            disabled={true}
                            loading={true}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                );
              },
              headerStyle: () => {
                return { width: '100px' };
              },
            },
            {
              text: t('common.edit'),
              dataField: '',
              headerAlign: 'center',
              align: 'center',
              style: { verticalAlign: 'middle' },
              formatter: (_cell, row: Contact) => {
                return (
                  <div>
                    <Button
                      size={WidgetSizeEnum.X_SMALL}
                      onClick={() => onClickEdit(row)}
                      data-testid="edit-btn"
                      extraClasses="rounded-full ring-0 ring-offset-0 shadow-none display-block"
                    >
                      <EditIcon className="size-6" />
                    </Button>
                    <Button
                      size={WidgetSizeEnum.X_SMALL}
                      onClick={() => onClickDelete(row)}
                      data-testid="delete-btn"
                      extraClasses="rounded-full ring-0 ring-offset-0 shadow-none display-block"
                    >
                      <DeleteIcon className="size-6" />
                    </Button>
                  </div>
                );
              },
              headerStyle: () => {
                return { width: '100px' };
              },
            },
          ]),
    ];
  }
}
